/** Per-client read reuse. No browser storage, global response cache, retries, or write deduplication. */
const aborted = () => new DOMException("The read is no longer needed", "AbortError");
interface Pending<T> {
  controller: AbortController;
  promise: Promise<T>;
  users: number;
  settled: boolean;
  retainFor: number;
  retainSignal?: AbortSignal;
  normalConsumer: boolean;
}

interface Cached<T> {
  value: T;
  until: number;
  once?: boolean;
  cleanup?: () => void;
  timer?: ReturnType<typeof setTimeout>;
}

export class ReadPool<T> {
  private readonly pending = new Map<string, Pending<T>>();
  private readonly active = new Set<Pending<T>>();
  private readonly cached = new Map<string, Cached<T>>();
  private generation = 0;
  private token: string | null = null;

  /** Check the SDK's token on every call, even a cache hit. A refresh drops reuse, but does not cancel the same session's consumers.
   * Real account/role/preview changes use the explicit /me scope reset; loss of session aborts. */
  identity(token: string | null): void {
    if (token !== this.token) { this.clear(token === null); this.token = token; }
  }

  /** Writes detach old reads so they cannot refill the cache; identity changes also abort consumers. */
  clear(abort = false): void {
    this.generation++;
    for (const key of this.cached.keys()) this.dropCached(key);
    if (abort) for (const entry of this.active) entry.controller.abort();
    this.pending.clear();
  }

  /** Drops the cached and not-yet-shared reads whose key matches, leaving every other read as it is (AIC-511). */
  forget(match: (key: string) => boolean): void {
    for (const key of [...this.cached.keys()]) if (match(key)) this.dropCached(key);
    for (const key of [...this.pending.keys()]) if (match(key)) this.pending.delete(key);
  }

  private dropCached(key: string): void {
    const cached = this.cached.get(key);
    if (!cached) return;
    cached.cleanup?.();
    if (cached.timer) clearTimeout(cached.timer);
    this.cached.delete(key);
  }

  read(key: string, fetcher: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal, ttl = 0, retainFor = 0): Promise<T> {
    if (signal?.aborted) return Promise.reject(aborted());
    const cached = this.cached.get(key);
    if (cached && cached.until > Date.now()) {
      if (cached.once) this.dropCached(key);
      return Promise.resolve(cached.value);
    }
    this.dropCached(key);
    let entry = this.pending.get(key);
    if (!entry) {
      const controller = new AbortController();
      const generation = this.generation;
      const next: Pending<T> = { controller, users: 0, settled: false, retainFor, retainSignal: signal, normalConsumer: retainFor === 0, promise: Promise.resolve().then(() => {
        if (controller.signal.aborted) throw aborted();
        return fetcher(controller.signal);
      }) };
      next.promise = next.promise.then((value) => {
        // Also reject if an injected transport ignores AbortSignal.
        if (controller.signal.aborted) throw aborted();
        if (generation === this.generation && ttl > 0) this.cached.set(key, { value, until: Date.now() + ttl });
        if (generation === this.generation && next.retainFor > 0 && !next.normalConsumer) {
          const cached: Cached<T> = { value, until: Date.now() + next.retainFor, once: true };
          const discard = () => this.dropCached(key);
          cached.cleanup = () => next.retainSignal?.removeEventListener("abort", discard);
          cached.timer = setTimeout(discard, next.retainFor);
          next.retainSignal?.addEventListener("abort", discard, { once: true });
          this.cached.set(key, cached);
        }
        return value;
      }).finally(() => {
        next.settled = true;
        this.active.delete(next);
        if (this.pending.get(key) === next) this.pending.delete(key);
      });
      this.active.add(next);
      this.pending.set(key, next);
      entry = next;
    }
    const shared = entry;
    // A screen which joins a still-pending prefetch already receives its result;
    // do not retain a second, stale one-use response after that screen unmounts.
    if (retainFor === 0) shared.normalConsumer = true;
    shared.users++;
    return new Promise<T>((resolve, reject) => {
      let done = false;
      const release = () => {
        if (done) return false;
        done = true;
        signal?.removeEventListener("abort", cancel);
        shared.controller.signal.removeEventListener("abort", cancel);
        if (--shared.users === 0 && !shared.settled) {
          if (this.pending.get(key) === shared) this.pending.delete(key);
          shared.controller.abort();
        }
        return true;
      };
      const cancel = () => { if (release()) reject(aborted()); };
      signal?.addEventListener("abort", cancel, { once: true });
      shared.controller.signal.addEventListener("abort", cancel, { once: true });
      shared.promise.then((value) => { if (release()) resolve(value); }, (error: unknown) => { if (release()) reject(error); });
    });
  }
}
